/**
 * PipelineConfigSection Component
 *
 * Section for configuring ELT pipeline settings including
 * database loading, transformation steps, and error handling.
 * Steps within each group (staging/production) are displayed as
 * horizontal tabs with left/right reordering (executed left to right).
 */

import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SqlEditor } from '../../../components/ui/SqlEditor';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { FormField } from '../../../components/forms/FormField';
import type {
  PipelineConfigurationDTO,
  TransformationStepCreateDTO,
  LoadErrorHandling,
} from '../../../types/elt-job.types';
import { PlusIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface PipelineConfigSectionProps {
  config: PipelineConfigurationDTO;
  onChange: (config: PipelineConfigurationDTO) => void;
}

export const PipelineConfigSection: React.FC<PipelineConfigSectionProps> = ({
  config,
  onChange,
}) => {
  const [activeGroup, setActiveGroup] = useState<'staging' | 'production'>('staging');
  const [activeStagingTab, setActiveStagingTab] = useState<number>(0);
  const [activeProductionTab, setActiveProductionTab] = useState<number>(0);

  const updateConfig = (updates: Partial<PipelineConfigurationDTO>) => {
    onChange({ ...config, ...updates });
  };

  const getSteps = (type: 'staging' | 'production') =>
    type === 'staging' ? config?.stagingSteps || [] : config?.productionSteps || [];

  const getActiveTab = (type: 'staging' | 'production') =>
    type === 'staging' ? activeStagingTab : activeProductionTab;

  const setActiveTab = (type: 'staging' | 'production', index: number) => {
    if (type === 'staging') {
      setActiveStagingTab(index);
    } else {
      setActiveProductionTab(index);
    }
  };

  const addStep = (type: 'staging' | 'production') => {
    if (!config) return;

    const steps = getSteps(type);
    const newStep: TransformationStepCreateDTO = {
      name: '',
      sql: '',
      orderIndex: steps.length,
      isActive: true,
      timeout: 300,
    };

    if (type === 'staging') {
      updateConfig({ stagingSteps: [...steps, newStep] });
      setActiveStagingTab(steps.length);
    } else {
      updateConfig({ productionSteps: [...steps, newStep] });
      setActiveProductionTab(steps.length);
    }
  };

  const updateStep = (
    type: 'staging' | 'production',
    index: number,
    updates: Partial<TransformationStepCreateDTO>
  ) => {
    if (!config) return;

    const steps = [...getSteps(type)];
    steps[index] = { ...steps[index], ...updates };

    if (type === 'staging') {
      updateConfig({ stagingSteps: steps });
    } else {
      updateConfig({ productionSteps: steps });
    }
  };

  const removeStep = (type: 'staging' | 'production', index: number) => {
    if (!config) return;

    const steps = getSteps(type);
    const filtered = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, orderIndex: i }));
    const currentTab = getActiveTab(type);

    if (type === 'staging') {
      updateConfig({ stagingSteps: filtered });
      if (currentTab >= filtered.length) {
        setActiveStagingTab(Math.max(0, filtered.length - 1));
      } else if (currentTab > index) {
        setActiveStagingTab(currentTab - 1);
      }
    } else {
      updateConfig({ productionSteps: filtered });
      if (currentTab >= filtered.length) {
        setActiveProductionTab(Math.max(0, filtered.length - 1));
      } else if (currentTab > index) {
        setActiveProductionTab(currentTab - 1);
      }
    }
  };

  const moveStep = (type: 'staging' | 'production', index: number, direction: 'left' | 'right') => {
    if (!config) return;

    const steps = [...getSteps(type)];
    if (
      (direction === 'left' && index === 0) ||
      (direction === 'right' && index === steps.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'left' ? index - 1 : index + 1;
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    const reordered = steps.map((s, i) => ({ ...s, orderIndex: i }));

    if (type === 'staging') {
      updateConfig({ stagingSteps: reordered });
      setActiveStagingTab(newIndex);
    } else {
      updateConfig({ productionSteps: reordered });
      setActiveProductionTab(newIndex);
    }
  };

  const renderSteps = (type: 'staging' | 'production') => {
    const steps = getSteps(type);
    const currentTab = getActiveTab(type);
    const activeStep = steps[currentTab];
    const label = type === 'staging' ? 'Landing → Staging' : 'Staging → Production';

    return (
      <div className="space-y-4">
        {/* Step tabs */}
        <div className="flex items-center gap-2 border-b border-gray-200">
          <div className="flex-1 flex items-center overflow-x-auto">
            {steps.map((step, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveTab(type, index)}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  currentTab === index
                    ? 'text-primary-600 border-primary-600 bg-primary-50/50'
                    : step.isActive === false
                    ? 'text-gray-400 border-transparent hover:text-gray-500 hover:border-gray-300'
                    : 'text-gray-600 border-transparent hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                  {index + 1}
                </span>
                <span className="max-w-[120px] truncate">
                  {step.name || `Step ${index + 1}`}
                </span>
                {step.isActive === false && (
                  <span className="text-xs text-gray-400">(off)</span>
                )}
              </button>
            ))}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => addStep(type)} className="shrink-0">
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Step
          </Button>
        </div>

        {/* Empty state */}
        {steps.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-sm text-gray-500">
              No {label.toLowerCase()} transformation steps defined.
            </p>
            <Button type="button" variant="primary" size="sm" className="mt-3" onClick={() => addStep(type)}>
              Add First Step
            </Button>
          </div>
        )}

        {/* Active step content */}
        {activeStep && (
          <div className="space-y-4">
            {/* Step toolbar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => moveStep(type, currentTab, 'left')}
                  disabled={currentTab === 0}
                  title="Move step left (executes earlier)"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => moveStep(type, currentTab, 'right')}
                  disabled={currentTab === steps.length - 1}
                  title="Move step right (executes later)"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-500">
                  Execution order: {currentTab + 1} of {steps.length}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={activeStep.isActive !== false}
                    onChange={(e) => updateStep(type, currentTab, { isActive: e.target.checked })}
                  />
                  Active
                </label>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removeStep(type, currentTab)}
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>

            {/* Step fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Step Name" required>
                <Input
                  value={activeStep.name}
                  onChange={(e) => updateStep(type, currentTab, { name: e.target.value })}
                  placeholder="dedupe_records"
                />
              </FormField>
              <FormField label="Timeout (seconds)">
                <Input
                  type="number"
                  value={activeStep.timeout || 300}
                  onChange={(e) => updateStep(type, currentTab, { timeout: parseInt(e.target.value) || 300 })}
                  min={1}
                  max={3600}
                />
              </FormField>
            </div>

            <FormField label="Description">
              <Input
                value={activeStep.description || ''}
                onChange={(e) => updateStep(type, currentTab, { description: e.target.value || undefined })}
                placeholder="Remove duplicate records based on primary key"
              />
            </FormField>

            <FormField label="SQL Statement" required hint="Use {{parameter}} for dynamic values. Multiple statements can be separated by semicolons.">
              <SqlEditor
                value={activeStep.sql}
                onChange={(value) => updateStep(type, currentTab, { sql: value })}
                placeholder="INSERT INTO staging.users SELECT DISTINCT ON (id) * FROM landing.users ORDER BY id, updated_at DESC"
                minHeight="160px"
                maxHeight="400px"
              />
            </FormField>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Pipeline Configuration</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure database loading and transformation steps for the ELT pipeline.
        </p>
      </div>

      <div className="space-y-6">
          {/* Basic Settings */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Load Batch Size" hint="Rows per batch insert">
              <Input
                type="number"
                value={config.loadBatchSize || 1000}
                onChange={(e) => updateConfig({ loadBatchSize: parseInt(e.target.value) || 1000 })}
                min={1}
                max={10000}
              />
            </FormField>

            <FormField label="Error Handling" hint="How to handle load errors">
              <Select
                value={config.errorHandling || 'ABORT'}
                onChange={(e) => updateConfig({ errorHandling: e.target.value as LoadErrorHandling })}
              >
                <option value="ABORT">Abort - Stop on first error</option>
                <option value="SKIP_ROW">Skip Row - Skip failed rows</option>
                <option value="LOG_CONTINUE">Log & Continue - Log errors and continue</option>
              </Select>
            </FormField>
          </div>

          {/* Transformation Steps - Staging/Production Group Toggle */}
          <div className="border border-gray-200 rounded-lg">
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                onClick={() => setActiveGroup('staging')}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  activeGroup === 'staging'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Staging Steps ({config.stagingSteps?.length || 0})
              </button>
              <button
                type="button"
                onClick={() => setActiveGroup('production')}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  activeGroup === 'production'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Production Steps ({config.productionSteps?.length || 0})
              </button>
            </div>

            <div className="p-4">
              {activeGroup === 'staging' && renderSteps('staging')}
              {activeGroup === 'production' && renderSteps('production')}
            </div>
          </div>
        </div>
    </div>
  );
};
